(function () {
  'use strict';

  if (customElements.get('ecomhard-popup')) return;

  const DAY = 24 * 60 * 60 * 1000;

  function safeGet(storage, key) {
    try { return storage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(storage, key, value) {
    try { storage.setItem(key, value); return true; } catch (e) { return false; }
  }
  function safeRemove(storage, key) {
    try { storage.removeItem(key); } catch (e) {}
  }
  function parseJSON(value, fallback) {
    if (!value) return fallback;
    try { return JSON.parse(value); } catch (e) { return fallback; }
  }

  class EcomhardPopup extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId || 'default';
      this.campaignId = (this.dataset.campaignId || 'default').trim() || 'default';
      this.frequency = this.dataset.frequency || 'twice_week';
      this.delay = Math.max(0, Number(this.dataset.delay || 0));
      this.customMax = Math.max(1, Number(this.dataset.customMax || 1));
      this.customPeriodDays = Math.max(1, Number(this.dataset.customPeriodDays || 1));
      this.closeCooldown = this.dataset.closeCooldown || 'session';
      this.closeOnOverlay = this.dataset.closeOnOverlay === 'true';
      this.showDesktop = this.dataset.showDesktop !== 'false';
      this.showMobile = this.dataset.showMobile !== 'false';

      this.baseKey = `ecomhard-popup:${this.sectionId}:${this.campaignId}`;
      this.viewsKey = `${this.baseKey}:views`;
      this.sessionViewKey = `${this.baseKey}:session-view`;
      this.sessionCloseKey = `${this.baseKey}:session-closed`;
      this.closedUntilKey = `${this.baseKey}:closed-until`;
      this.closedCampaignKey = `${this.baseKey}:closed-campaign`;

      this.panel = this.querySelector('[data-ec-popup-panel]');
      this.backdrop = this.querySelector('[data-ec-popup-backdrop]');
      this.closeButtons = this.querySelectorAll('[data-ec-popup-close]');
      this._previousFocus = null;
      this._timer = null;
      this._animationTimer = null;
      this._panelAnimation = null;
      this._backdropAnimation = null;

      this._boundKeydown = this._onKeydown.bind(this);
      this._boundBackdrop = this._onBackdropClick.bind(this);
      this._boundClose = this.close.bind(this);

      this.closeButtons.forEach((button) => button.addEventListener('click', this._boundClose));
      if (this.backdrop) this.backdrop.addEventListener('click', this._boundBackdrop);

      if (window.Shopify && Shopify.designMode) {
        this._setupThemeEditor();
        this._timer = window.setTimeout(() => this.open({ recordView: false, force: true }), 80);
        return;
      }

      if (!this._matchesDevice()) return;
      if (!this._canShow()) return;

      this._timer = window.setTimeout(() => {
        if (this._matchesDevice() && this._canShow()) this.open();
      }, this.delay * 1000);
    }

    disconnectedCallback() {
      if (this._timer) window.clearTimeout(this._timer);
      if (this._animationTimer) window.clearTimeout(this._animationTimer);
      this._cancelAnimations();
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this._boundClose));
      if (this.backdrop) this.backdrop.removeEventListener('click', this._boundBackdrop);
      document.removeEventListener('keydown', this._boundKeydown);
      document.body.classList.remove('ecomhard-popup-open');
    }

    _setupThemeEditor() {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail && event.detail.sectionId === this.sectionId) {
          this.open({ recordView: false, force: true });
        }
      });
      document.addEventListener('shopify:block:select', (event) => {
        if (this.contains(event.target)) this.open({ recordView: false, force: true });
      });
    }

    _matchesDevice() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      return isMobile ? this.showMobile : this.showDesktop;
    }

    _isClosed() {
      if (safeGet(sessionStorage, this.sessionCloseKey) === '1') return true;
      if (safeGet(localStorage, this.closedCampaignKey) === '1') return true;

      const closedUntil = Number(safeGet(localStorage, this.closedUntilKey) || 0);
      if (closedUntil > Date.now()) return true;
      if (closedUntil && closedUntil <= Date.now()) safeRemove(localStorage, this.closedUntilKey);

      return false;
    }

    _getFrequencyRule() {
      switch (this.frequency) {
        case 'once_session': return { session: true, max: 1, period: 0 };
        case 'once_day': return { max: 1, period: DAY };
        case 'twice_day': return { max: 2, period: DAY };
        case 'once_week': return { max: 1, period: 7 * DAY };
        case 'twice_week': return { max: 2, period: 7 * DAY };
        case 'custom': return { max: this.customMax, period: this.customPeriodDays * DAY };
        case 'every_access':
        default: return { max: Infinity, period: 0 };
      }
    }

    _canShow() {
      if (this._isClosed()) return false;

      const rule = this._getFrequencyRule();
      if (rule.session) {
        return safeGet(sessionStorage, this.sessionViewKey) !== '1';
      }
      if (!Number.isFinite(rule.max)) return true;

      const now = Date.now();
      let views = parseJSON(safeGet(localStorage, this.viewsKey), []);
      if (!Array.isArray(views)) views = [];
      views = views.map(Number).filter((ts) => Number.isFinite(ts) && ts > now - rule.period);
      safeSet(localStorage, this.viewsKey, JSON.stringify(views));
      return views.length < rule.max;
    }

    _recordView() {
      const rule = this._getFrequencyRule();
      if (rule.session) {
        safeSet(sessionStorage, this.sessionViewKey, '1');
        return;
      }
      if (!Number.isFinite(rule.max)) return;

      const now = Date.now();
      let views = parseJSON(safeGet(localStorage, this.viewsKey), []);
      if (!Array.isArray(views)) views = [];
      views = views.map(Number).filter((ts) => Number.isFinite(ts) && ts > now - rule.period);
      views.push(now);
      safeSet(localStorage, this.viewsKey, JSON.stringify(views));
    }

    _recordClose() {
      safeSet(sessionStorage, this.sessionCloseKey, '1');

      let days = 0;
      switch (this.closeCooldown) {
        case '1_day': days = 1; break;
        case '3_days': days = 3; break;
        case '7_days': days = 7; break;
        case '30_days': days = 30; break;
        case 'campaign':
          safeSet(localStorage, this.closedCampaignKey, '1');
          return;
        case 'session':
        default:
          return;
      }
      safeSet(localStorage, this.closedUntilKey, String(Date.now() + days * DAY));
    }

    _prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    _supportsGenie() {
      return !!(
        this.panel &&
        typeof this.panel.animate === 'function' &&
        window.CSS &&
        typeof CSS.supports === 'function' &&
        CSS.supports('clip-path', 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)')
      );
    }

    _cancelAnimations() {
      if (this._panelAnimation) {
        try { this._panelAnimation.cancel(); } catch (e) {}
        this._panelAnimation = null;
      }
      if (this._backdropAnimation) {
        try { this._backdropAnimation.cancel(); } catch (e) {}
        this._backdropAnimation = null;
      }
      if (this.panel && typeof this.panel.getAnimations === 'function') {
        this.panel.getAnimations().forEach((animation) => {
          try { animation.cancel(); } catch (e) {}
        });
      }
      if (this.backdrop && typeof this.backdrop.getAnimations === 'function') {
        this.backdrop.getAnimations().forEach((animation) => {
          try { animation.cancel(); } catch (e) {}
        });
      }
    }

    _focusPopup() {
      window.setTimeout(() => {
        const focusTarget = this.querySelector('[data-ec-popup-close], a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        else if (this.panel) this.panel.focus({ preventScroll: true });
      }, 80);
    }

    _animateOpen() {
      if (!this.panel || this._prefersReducedMotion()) {
        this._focusPopup();
        return;
      }

      this._cancelAnimations();

      if (this.backdrop && typeof this.backdrop.animate === 'function') {
        this._backdropAnimation = this.backdrop.animate(
          [
            { opacity: 0 },
            { opacity: 1 }
          ],
          {
            duration: 420,
            easing: 'cubic-bezier(.2,.75,.2,1)',
            fill: 'none'
          }
        );
      }

      const fallbackFrames = [
        { opacity: 0, transform: 'translate3d(0, 150px, 0) scale(.82, .18)' },
        { opacity: .72, transform: 'translate3d(0, 34px, 0) scale(.94, .84)', offset: .62 },
        { opacity: 1, transform: 'translate3d(0, -3px, 0) scale(1.005, 1.008)', offset: .88 },
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1, 1)' }
      ];

      /*
       * Genie mais marcado: a base nasce em um "pescoço" bem estreito e
       * abre em leque. Mantemos a parte superior mais larga por mais tempo e
       * comprimimos principalmente o terço inferior para evitar o aspecto
       * quadrado durante a animação.
       */
      const genieFrames = [
        {
          opacity: 0,
          transform: 'translate3d(0, 205px, 0) scale(.82, .11)',
          clipPath: 'polygon(43% 0%, 57% 0%, 55% 35%, 52% 72%, 50.8% 100%, 49.2% 100%, 48% 72%, 45% 35%)'
        },
        {
          opacity: .4,
          transform: 'translate3d(0, 122px, 0) scale(.9, .43)',
          clipPath: 'polygon(23% 0%, 77% 0%, 72% 38%, 61% 73%, 53% 100%, 47% 100%, 39% 73%, 28% 38%)',
          offset: .28
        },
        {
          opacity: .86,
          transform: 'translate3d(0, 34px, 0) scale(.985, .86)',
          clipPath: 'polygon(5% 0%, 95% 0%, 91% 43%, 77% 78%, 61% 100%, 39% 100%, 23% 78%, 9% 43%)',
          offset: .68
        },
        {
          opacity: 1,
          transform: 'translate3d(0, -4px, 0) scale(1.006, 1.01)',
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 52%, 96% 82%, 91% 100%, 9% 100%, 4% 82%, 0% 52%)',
          offset: .9
        },
        {
          opacity: 1,
          transform: 'translate3d(0, 0, 0) scale(1, 1)',
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 100%, 0% 100%, 0% 100%, 0% 100%)'
        }
      ];

      this._panelAnimation = this.panel.animate(
        this._supportsGenie() ? genieFrames : fallbackFrames,
        {
          duration: 760,
          easing: 'cubic-bezier(.16, 1, .3, 1)',
          fill: 'none'
        }
      );

      this._panelAnimation.finished
        .catch(() => {})
        .finally(() => {
          this._panelAnimation = null;
          this._focusPopup();
        });
    }

    _animateClose(done) {
      if (!this.panel || this._prefersReducedMotion()) {
        done();
        return;
      }

      this._cancelAnimations();

      if (this.backdrop && typeof this.backdrop.animate === 'function') {
        this._backdropAnimation = this.backdrop.animate(
          [
            { opacity: 1 },
            { opacity: .92, offset: .38 },
            { opacity: 0 }
          ],
          {
            duration: 560,
            easing: 'cubic-bezier(.4,0,.7,.2)',
            fill: 'forwards'
          }
        );
      }

      const fallbackFrames = [
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1, 1)' },
        { opacity: .8, transform: 'translate3d(0, 30px, 0) scale(.95, .86)', offset: .42 },
        { opacity: .3, transform: 'translate3d(0, 115px, 0) scale(.84, .40)', offset: .78 },
        { opacity: 0, transform: 'translate3d(0, 190px, 0) scale(.74, .12)' }
      ];

      /* Fechamento Genie: primeiro forma o funil na base e só depois suga o topo. */
      const genieFrames = [
        {
          opacity: 1,
          transform: 'translate3d(0, 0, 0) scale(1, 1)',
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 100%, 0% 100%, 0% 100%, 0% 100%)'
        },
        {
          opacity: .98,
          transform: 'translate3d(0, 10px, 0) scale(.995, .97)',
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 48%, 92% 80%, 76% 100%, 24% 100%, 8% 80%, 0% 48%)',
          offset: .22
        },
        {
          opacity: .8,
          transform: 'translate3d(0, 62px, 0) scale(.96, .72)',
          clipPath: 'polygon(8% 0%, 92% 0%, 88% 36%, 72% 72%, 57% 100%, 43% 100%, 28% 72%, 12% 36%)',
          offset: .54
        },
        {
          opacity: .34,
          transform: 'translate3d(0, 145px, 0) scale(.88, .31)',
          clipPath: 'polygon(26% 0%, 74% 0%, 69% 34%, 59% 70%, 52.5% 100%, 47.5% 100%, 41% 70%, 31% 34%)',
          offset: .82
        },
        {
          opacity: 0,
          transform: 'translate3d(0, 220px, 0) scale(.82, .08)',
          clipPath: 'polygon(44% 0%, 56% 0%, 54% 34%, 51.5% 70%, 50.6% 100%, 49.4% 100%, 48.5% 70%, 46% 34%)'
        }
      ];

      this._panelAnimation = this.panel.animate(
        this._supportsGenie() ? genieFrames : fallbackFrames,
        {
          duration: 680,
          easing: 'cubic-bezier(.45,0,.55,1)',
          fill: 'forwards'
        }
      );

      let completed = false;
      const finish = () => {
        if (completed) return;
        completed = true;
        this._panelAnimation = null;
        done();
      };

      this._panelAnimation.finished.then(finish).catch(finish);
      this._animationTimer = window.setTimeout(finish, 760);
    }

    open(options) {
      const opts = options || {};
      if (this.classList.contains('is-open') && !this.classList.contains('is-closing')) return;
      if (!opts.force && (!this._matchesDevice() || !this._canShow())) return;

      if (opts.recordView !== false) this._recordView();

      this._previousFocus = document.activeElement;
      if (this._animationTimer) {
        window.clearTimeout(this._animationTimer);
        this._animationTimer = null;
      }
      this._cancelAnimations();
      this.classList.remove('is-closing');
      this.hidden = false;
      document.body.classList.add('ecomhard-popup-open');
      document.addEventListener('keydown', this._boundKeydown);

      /*
        Duplo frame garante que o navegador primeiro retire [hidden] e faça o
        layout. Depois iniciamos a animação explicitamente; assim a abertura
        funciona inclusive no primeiro carregamento e no editor da Shopify.
      */
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        requestAnimationFrame(() => this._animateOpen());
      });
    }

    close() {
      if ((!this.classList.contains('is-open') && this.hidden) || this.classList.contains('is-closing')) return;
      if (!(window.Shopify && Shopify.designMode)) this._recordClose();

      if (this._animationTimer) {
        window.clearTimeout(this._animationTimer);
        this._animationTimer = null;
      }
      document.removeEventListener('keydown', this._boundKeydown);
      this.classList.add('is-closing');

      this._animateClose(() => {
        if (this._animationTimer) {
          window.clearTimeout(this._animationTimer);
          this._animationTimer = null;
        }
        this._cancelAnimations();
        this.classList.remove('is-open', 'is-closing');
        this.hidden = true;
        document.body.classList.remove('ecomhard-popup-open');

        if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
          try { this._previousFocus.focus({ preventScroll: true }); } catch (e) {}
        }
      });
    }

    _onBackdropClick(event) {
      if (!this.closeOnOverlay) return;
      if (event.target === this.backdrop) this.close();
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.panel) return;

      const focusable = Array.from(this.panel.querySelectorAll(
        'a[href]:not([aria-disabled="true"]), button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((el) => el.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('ecomhard-popup', EcomhardPopup);
})();
